import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Button,
  Input,
  Label,
  Modal,
  Select,
  showToast,
  Text,
  WarningOutlineIcon,
} from '@mercai/clever'

import { useValidationErrors } from '@/helpers'
import { QUERIES_ADMIN, useOrderCenterCommentsCreate } from '@/services'

import {
  getContentByType,
  schema,
  typesEnum,
} from './modal-order-infos-change-warning.constants'

import {
  ModalOrderInfosChangeWarningProps,
  ModalOrderInfosChangeWarningTypeEnum,
} from './modal-order-infos-change-warning.type'

import styles from './modal-order-infos-change-warning.module.css'

export const ModalOrderInfosChangeWarning = ({
  saleOrderUuid = '',
  isOpen,
  onClose,
  onSave,
  types,
}: ModalOrderInfosChangeWarningProps) => {
  const { t } = useTranslation('componentCardsOrderCenterShow')
  const queryClient = useQueryClient()

  const [step, setStep] = useState<number>(0)
  const [stepValues, setStepValues] = useState<
    {
      reason: string
      comment: string
    }[]
  >([])

  const onChangeStepValues = (index: number, field: string, value: string) => {
    const newValues = [...stepValues]
    newValues[index] = {
      ...newValues[index],
      [field]: value,
    }
    setStepValues(newValues)
  }

  const { mutateAsync, isPending } = useOrderCenterCommentsCreate(saleOrderUuid)

  const { errors, isValid, onHandleListenErrors } = useValidationErrors(
    schema(t),
    stepValues[step],
  )

  const contentByType = getContentByType(t)

  const onSubmit = async () => {
    if (!types) {
      return
    }

    if (!isValid) {
      onHandleListenErrors(true)
      return
    }

    onHandleListenErrors(false)

    const requests = types.map((type, index) =>
      mutateAsync({
        type: typesEnum[type],
        subject: stepValues[index].reason,
        comment: stepValues[index].comment,
      }),
    )

    const responses = await Promise.all(requests)

    const responseError = responses.find((response) => !response.success)

    if (responseError) {
      showToast({
        variant: 'error',
        position: 'bottom-left',
        title:
          responseError?.message ||
          t('orderStatusesModalWarningSubmitErrorDescription'),
      })

      return
    }

    queryClient.invalidateQueries({
      queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, saleOrderUuid],
    })

    onSave()
  }

  const nextStep = (index: number) => {
    if (!isValid) {
      onHandleListenErrors(true)
      return
    }

    onHandleListenErrors(false)

    setStep(index + 1)
  }

  const backStep = (index: number) => {
    if (index === 0) return
    setStep(index - 1)
  }

  const handleClose = () => {
    setStep(0)
    onClose()
  }

  const getStepComponent = (
    type: keyof typeof ModalOrderInfosChangeWarningTypeEnum,
    index: number,
    count: number,
  ) => {
    const hasOnlyOne = count === 0
    const isFirst = index === 0
    const isLast = index === count

    console.log(contentByType?.[type], type)

    return {
      title: (
        <div className={styles['modal-header']}>
          <Text variant="text-large-bold" className={styles.title}>
            <span>{`${index + 1}/${count + 1}`}</span>
          </Text>
        </div>
      ),
      component: (
        <div className={styles.component}>
          <div className={styles.description}>
            <div className={styles.icon}>
              <WarningOutlineIcon />
            </div>
            <Text variant="text-large-bold" className={styles.title}>
              <span>
                {contentByType?.[type]?.title}
                {contentByType?.[type]?.field}
              </span>
            </Text>
            <Text>{contentByType?.[type]?.description}</Text>
          </div>
          <div>
            <Label required>
              {t('orderStatusesModalInfosChangeReasonLabel')}
            </Label>
            <Select
              placeholder={t('orderStatusesModalInfosChangeReasonPlaceholder')}
              options={contentByType?.[type]?.reasons}
              value={stepValues?.[index]?.reason}
              onChange={(reason) => onChangeStepValues(index, 'reason', reason)}
              error={errors?.reason ? errors?.reason : undefined}
            />
          </div>

          <div>
            <Label required>
              {t('orderStatusesModalInfosChangeCommentLabel')}
            </Label>
            <Input
              isTextArea={true}
              value={stepValues?.[index]?.comment}
              onChange={(comment) =>
                onChangeStepValues(index, 'comment', comment)
              }
              error={errors?.comment ? errors?.comment : undefined}
            />
          </div>

          <div className={styles.footer}>
            <Button
              variant="secondary"
              onClick={() =>
                isFirst || hasOnlyOne ? handleClose() : backStep(index)
              }
            >
              {isFirst || hasOnlyOne
                ? t('orderStatusesModalInfosCancelButton')
                : t('orderStatusesModalInfosBackButton')}
            </Button>

            <Button
              variant="primary"
              onClick={() =>
                isLast || hasOnlyOne ? onSubmit() : nextStep(index)
              }
              isActive={!isPending}
              isLoading={isPending}
            >
              {isLast || hasOnlyOne
                ? t('orderStatusesModalInfosSaveButton')
                : t('orderStatusesModalInfosNextButton')}
            </Button>
          </div>
        </div>
      ),
    }
  }

  const steps = types
    ? types?.map((type, index) =>
        getStepComponent(type, index, types.length - 1),
      )
    : []

  useEffect(() => {
    setStepValues(
      types
        ? types.map(() => ({
            reason: '',
            comment: '',
          }))
        : [],
    )
  }, [types])

  return (
    <Modal.Root
      isOpen={isOpen}
      onHandleOpen={handleClose}
      size="small"
      mainClassName={styles['modal-body']}
    >
      <Modal.Header
        title={steps?.[step]?.title || ''}
        className={styles.header}
      />
      <Modal.Body className={styles.body}>
        {steps?.[step]?.component}
      </Modal.Body>
    </Modal.Root>
  )
}
